import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Truck } from "lucide-react";

const EMPTY_FORM = {
  name: "", email: "", practice: "", password: "",
  address_line1: "", address_line2: "", city: "", state: "", zip_code: "", phone: "",
};

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Request submitted — awaiting admin approval.");
      navigate("/paywall");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <Link to="/" className="block mb-8 text-center" data-testid="signup-brand-link">
          <img src="/gcoms-logo.png" alt="GCOMS" className="w-16 h-16 mx-auto rounded-full bg-black p-1" />
          <div className="font-heading text-2xl font-bold tracking-tight mt-3">GCOMS.RN</div>
        </Link>
        <div className="module-card p-10 bg-white">
          <div className="uppercase-label">Request access</div>
          <h1 className="font-heading text-3xl font-semibold mt-3">Join the network.</h1>
          <p className="text-sm text-muted-foreground mt-2">Licensed dental professionals only.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label="Full name *" testid="signup-name-input" type="text" required v={form.name} on={(v) => update("name", v)} />
            <Field label="Professional email *" testid="signup-email-input" type="email" required v={form.email} on={(v) => update("email", v)} />
            <Field label="Practice / clinic" testid="signup-practice-input" type="text" v={form.practice} on={(v) => update("practice", v)} />
            <Field label="Password (min 8 chars) *" testid="signup-password-input" type="password" required minLength={8} v={form.password} on={(v) => update("password", v)} />

            {/* Shipping info — collected once so pad requests are zero-typing */}
            <div className="pt-3 mt-3 border-t border-ink/10">
              <div className="flex items-center gap-2 mb-1">
                <Truck size={14} className="text-[#F26A24]" />
                <div className="text-sm font-medium">Shipping info <span className="text-muted-foreground font-normal">(optional — speeds up future referral-pad requests)</span></div>
              </div>
              <p className="text-xs text-muted-foreground mb-3">We'll use this to auto-fill your referral-pad reorder form so you never type it twice.</p>
              <div className="space-y-3">
                <Field label="Address line 1" testid="signup-addr1-input" type="text" v={form.address_line1} on={(v) => update("address_line1", v)} />
                <Field label="Address line 2" testid="signup-addr2-input" type="text" v={form.address_line2} on={(v) => update("address_line2", v)} />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City" testid="signup-city-input" type="text" v={form.city} on={(v) => update("city", v)} />
                  <Field label="State" testid="signup-state-input" type="text" v={form.state} on={(v) => update("state", v)} />
                  <Field label="ZIP" testid="signup-zip-input" type="text" v={form.zip_code} on={(v) => update("zip_code", v)} />
                </div>
                <Field label="Phone" testid="signup-phone-input" type="tel" v={form.phone} on={(v) => update("phone", v)} />
              </div>
            </div>

            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3 rounded-md bg-ink text-bone font-medium hover:bg-ink/90 disabled:opacity-60"
            >
              {loading ? "Creating..." : "Create account"}
            </button>
          </form>
          <div className="mt-6 text-sm text-muted-foreground text-center">
            Already a member? <Link to="/login" data-testid="signup-login-link" className="text-ink underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", required, minLength, v, on }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        minLength={minLength}
        value={v}
        onChange={(e) => on(e.target.value)}
        className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
      />
    </div>
  );
}
